import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Paper,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Box,
  Grid,
  Stack,
  Divider
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  ArrowBack as ArrowBackIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import axios from '../utils/axios';
import { Link as RouterLink } from 'react-router-dom';

const ParishRegistrationSettings = () => {
  const [settings, setSettings] = useState(null);
  const [originalSettings, setOriginalSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [addRecipientDialog, setAddRecipientDialog] = useState(false);
  const [newRecipient, setNewRecipient] = useState('');

  const deepCompare = (obj1, obj2) => {
    return JSON.stringify(obj1) === JSON.stringify(obj2);
  };

  const hasChanges = !deepCompare(settings, originalSettings);

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get('/api/parish-registration/settings');
      setSettings(response.data);
      setOriginalSettings(JSON.parse(JSON.stringify(response.data)));
    } catch (err) {
      console.error('Error fetching parish registration settings:', err);
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError(null);
      const response = await axios.put('/api/parish-registration/settings', settings);
      setSettings(response.data);
      setOriginalSettings(JSON.parse(JSON.stringify(response.data)));
      setSuccess('Settings saved successfully');
    } catch (err) {
      console.error('Error saving parish registration settings:', err);
      setError('Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setSettings(JSON.parse(JSON.stringify(originalSettings)));
  };

  const handleToggleRegistration = (enabled) => {
    setSettings(prev => ({
      ...prev,
      registrationEnabled: enabled
    }));
  };

  const handleDisabledMessageChange = (message) => {
    setSettings(prev => ({
      ...prev,
      disabledMessage: message
    }));
  };

  const handleToggleNotifications = (enabled) => {
    setSettings(prev => ({
      ...prev,
      emailNotifications: {
        ...prev.emailNotifications,
        newRegistration: {
          ...prev.emailNotifications?.newRegistration,
          enabled: enabled
        }
      }
    }));
  };


  const handleAddRecipient = () => {
    if (newRecipient.trim() && isValidEmail(newRecipient.trim())) {
      setSettings(prev => ({
        ...prev,
        emailNotifications: {
          ...prev.emailNotifications,
          newRegistration: {
            ...prev.emailNotifications?.newRegistration,
            recipients: [...(prev.emailNotifications?.newRegistration?.recipients || []), newRecipient.trim()]
          }
        }
      }));
      setNewRecipient('');
      setAddRecipientDialog(false);
    }
  };

  const handleRemoveRecipient = (index) => {
    setSettings(prev => ({
      ...prev,
      emailNotifications: {
        ...prev.emailNotifications,
        newRegistration: {
          ...prev.emailNotifications?.newRegistration,
          recipients: (prev.emailNotifications?.newRegistration?.recipients || []).filter((_, i) => i !== index)
        }
      }
    }));
  };

  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  if (loading) {
    return (
      <Container>
        <Typography>Loading settings...</Typography>
      </Container>
    );
  }

  if (!settings) {
    return (
      <Container>
        <Alert severity="error">Failed to load settings</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          component={RouterLink}
          to="/parish-registration"
          startIcon={<ArrowBackIcon />}
          variant="outlined"
        >
          Back to Parish Registrations
        </Button>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SettingsIcon color="primary" />
          <Typography variant="h4">Parish Registration Settings</Typography>
        </Box>
      </Box>

      {/* Registration Status */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Registration Status
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={settings.registrationEnabled}
              onChange={(e) => handleToggleRegistration(e.target.checked)}
              color="primary"
            />
          }
          label={settings.registrationEnabled ? 'Registrations Enabled' : 'Registrations Disabled'}
        />

        <Box sx={{ mt: 2 }}>
          <TextField
            fullWidth
            label="Message When Registrations Are Disabled"
            multiline
            rows={3}
            value={settings.disabledMessage}
            onChange={(e) => handleDisabledMessageChange(e.target.value)}
            helperText="This message will be shown to users when registrations are disabled"
          />
        </Box>
      </Paper>

      {/* Email Notifications */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Email Notifications
        </Typography>


        {/* New Registration Notifications */}
        <Box sx={{ mb: 3 }}>
          <FormControlLabel
            control={
              <Switch
                checked={settings.emailNotifications?.newRegistration?.enabled || false}
                onChange={(e) => handleToggleNotifications(e.target.checked)}
                color="primary"
              />
            }
            label="New Registration Notifications"
          />
          <Typography variant="body2" color="textSecondary" sx={{ ml: 5 }}>
            Send notifications when new parish registrations are submitted
          </Typography>
        </Box>

        {/* Email Recipients */}
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1">Notification Recipients</Typography>
            <Button
              startIcon={<AddIcon />}
              onClick={() => setAddRecipientDialog(true)}
              variant="outlined"
              size="small"
            >
              Add Email
            </Button>
          </Box>

          {(settings.emailNotifications?.newRegistration?.recipients?.length || 0) === 0 ? (
            <Alert severity="warning">
              No email recipients configured. No notifications will be sent.
            </Alert>
          ) : (
            <List>
              {(settings.emailNotifications?.newRegistration?.recipients || []).map((email, index) => (
                <ListItem key={index} divider>
                  <ListItemText primary={email} />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      onClick={() => handleRemoveRecipient(index)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Paper>

      {/* Save/Cancel Actions */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          onClick={handleCancel}
          disabled={!hasChanges || isSaving}
          startIcon={<CancelIcon />}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!hasChanges || isSaving}
          startIcon={<SaveIcon />}
        >
          {isSaving ? 'Saving...' : 'Save Settings'}
        </Button>
      </Box>

      {/* Add Recipient Dialog */}
      <Dialog open={addRecipientDialog} onClose={() => setAddRecipientDialog(false)}>
        <DialogTitle>Add Email Recipient</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Email Address"
            type="email"
            fullWidth
            variant="outlined"
            value={newRecipient}
            onChange={(e) => setNewRecipient(e.target.value)}
            error={newRecipient.trim() !== '' && !isValidEmail(newRecipient.trim())}
            helperText={
              newRecipient.trim() !== '' && !isValidEmail(newRecipient.trim())
                ? 'Please enter a valid email address'
                : ''
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddRecipientDialog(false)}>Cancel</Button>
          <Button
            onClick={handleAddRecipient}
            disabled={!newRecipient.trim() || !isValidEmail(newRecipient.trim())}
            variant="contained"
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbars */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
      >
        <Alert onClose={() => setSuccess(null)} severity="success">
          {success}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert onClose={() => setError(null)} severity="error">
          {error}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ParishRegistrationSettings;